import { Button } from '@odekoteam/doppio';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { Flex, Heading, Input, Select, ThemeUICSSObject } from 'theme-ui';
import { useClient } from 'urql';

import IconPreview from '@/assets/images/IconPreview.svg?icon';
import { todayString } from '@/components/Collection/utils';
import Modal from '@/components/Modal';
import { GET_WAREHOUSE_LOCATIONS } from '@/graphql/requests/queries';
import {
  GetWarehouseLocationsQuery,
  GetWarehouseLocationsQueryVariables,
  GetWarehousesQuery,
} from '@/graphql/types/generated';

export function ModalPreview({
  warehouses,
  triggerWrapperSx,
  triggerVariant = 'tertiary',
}: {
  triggerWrapperSx?: ThemeUICSSObject;
  triggerVariant?: 'ghost' | 'tertiary';
  warehouses: GetWarehousesQuery['warehouses'][0][];
}) {
  const client = useClient();
  const [previewDate, setPreviewDate] = useState(todayString());
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  // The portal catalog is addressed by location id, so translate the selected
  // market to one of its locations on demand rather than over-fetching
  // locations for every warehouse up front.
  const onSubmit = async () => {
    setIsOpening(true);
    try {
      const { data } = await client
        .query<GetWarehouseLocationsQuery, GetWarehouseLocationsQueryVariables>(
          GET_WAREHOUSE_LOCATIONS,
          { warehouseId: selectedWarehouseId },
        )
        .toPromise();
      const locationId = data?.warehouse?.locations?.[0]?.id;
      if (!locationId) {
        return;
      }
      window.open(
        `${window.env.PORTAL_BASE_URL}/supply/locations/${locationId}/catalog/?date=${previewDate}`,
        '_blank',
      );
      setPreviewModalVisible(false);
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <div sx={triggerWrapperSx}>
      <Button
        size="small"
        variant={triggerVariant}
        onPress={() => setPreviewModalVisible(true)}
        iconRight={<IconPreview />}
      >
        Portal Live Preview
      </Button>
      <Modal visible={previewModalVisible}>
        <Form
          onSubmit={onSubmit}
          render={({ handleSubmit }) => (
            <>
              <Heading variant="headingSm" sx={{ mb: 3 }}>
                Portal Live Preview
              </Heading>
              <Flex sx={{ gap: 4, flexDirection: 'column' }}>
                <div>
                  <div sx={{ flex: 1, mb: 3 }}>
                    <Heading variant="headingXs">Date</Heading>
                    <Input
                      type="date"
                      id="preview-date"
                      min={todayString()}
                      value={previewDate}
                      onChange={(event) => {
                        setPreviewDate(event.target.value);
                        setSelectedWarehouseId('');
                      }}
                    />
                  </div>
                  <div sx={{ flex: 1 }}>
                    <Heading variant="headingXs">Market</Heading>
                    <Select
                      value={selectedWarehouseId}
                      sx={{
                        pl: 2,
                        pr: 8,
                        mr: 1,
                        height: 40,
                        width: '100%',
                        borderColor: 'secondaryHighlight',
                      }}
                      onChange={(event) =>
                        setSelectedWarehouseId(event.target.value)
                      }
                    >
                      <option value="">Select a market…</option>
                      {warehouses
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((w) => (
                          <option key={w.id} value={w.id}>
                            {w.name}
                          </option>
                        ))}
                    </Select>
                  </div>
                </div>
                <Flex sx={{ gap: 3, justifyContent: 'flex-end' }}>
                  <Button
                    variant="ghost"
                    onPress={() => setPreviewModalVisible(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onPress={handleSubmit}
                    isDisabled={!selectedWarehouseId || isOpening}
                  >
                    {isOpening ? 'Opening…' : 'Open Preview'}
                  </Button>
                </Flex>
              </Flex>
            </>
          )}
        />
      </Modal>
    </div>
  );
}
